import React from "react";
import PageContainer from "@components/page/PageContainer";
import {
  PaddingContainer,
  TermsHead1,
  TermsHead2,
  TermsParagraph,
} from "@helpers/style/global.styles";
import { Box } from "@mui/material";

const Screen: React.FC = () => {
  return (
    <PageContainer title="Terms &amp; conditions">
      <PaddingContainer>
        <Box sx={{ maxWidth: "65rem", margin: "auto" }}>
          <TermsHead1>Master Service Agreement</TermsHead1>

          <TermsParagraph>
            This Master Service Agreement (“Agreement”) is between Bunt Inc
            (“Provider”, incorporated in Delaware and with its headquarters in
            New York), and the entity (“Customer”) that has purchased a
            subscription to the TestTheTest service (“TestTheTest” or “Service”)
            provided by Provider.
          </TermsParagraph>
          <TermsParagraph>
            Document version 1.1, last updated on May 31, 2022.
          </TermsParagraph>
          <TermsParagraph>
            The terms of this agreement could be agreed to either by (1)
            explicitly clicking a box indicating acceptance, or (2) executing an
            Order Form, or (3) executing a Statement of Work (&quot;SOW&quot;)
            that references this agreement. Moreover, if Customer registers for
            a trial of TestTheTest or its free Services, the applicable
            provisions of this agreement will also govern that relationship.
          </TermsParagraph>

          <TermsHead2>Definitions</TermsHead2>

          <TermsParagraph>
            “Effective Date” of this Agreement is the Effective Date as stated
            on the Order Form, in the SOW, or the date Customer accepted this
            Agreement, whichever date is earlier.
          </TermsParagraph>
          <TermsParagraph>
            If Customer is an individual using Services on behalf of a
            corporation, partnership or other entity, then that entity will be
            the Customer, and the individual represents and warrants that they
            are authorized to enter into this Agreement on behalf of such
            entity, and bind such entity to the terms and conditions of this
            Agreement.
          </TermsParagraph>
          <TermsParagraph>
            Provider and Customer together are “Parties”
          </TermsParagraph>
          <TermsParagraph>
            “Customer Data” means any business information or other data which
            Customer inputs, or makes available for inputting into TestTheTest
            Services.
          </TermsParagraph>

          <TermsHead2>1. Service</TermsHead2>

          <TermsParagraph>
            TestTheTest is provided as a cloud service on a subscription basis
            for a set term (see the “Subscription Term”). Services include
            ongoing quality assurance testing, including feature discovery and
            bug reports provided to Customer on a regular basis. Reports will be
            provided on a regular basis, where the Reporting Frequency and the
            number of such reports during the Subscription Term are governed by
            the Order Form or SOW.
          </TermsParagraph>
          <TermsParagraph>
            Communication. Customer agrees to identify an administrative user
            and email address to receive TestTheTest reporting and other
            communication.
          </TermsParagraph>
          <TermsParagraph>
            Customer shall make available its systems for Provider only to
            conduct its activities related to Services. Customer acknowledges
            and agrees that no personally identifiable information is required
            for the TestTheTest Services and will use its commercially
            reasonable efforts to prevent sharing of such information with
            Provider.
          </TermsParagraph>
          <TermsParagraph>
            Customer shall retain all right, title and interest (including any
            and all intellectual property rights) in and to the Customer Data as
            provided to TestTheTest. Subject to the terms of this Agreement,
            Customer hereby grants to TestTheTest a non-exclusive, worldwide,
            royalty-free right to use, copy, store, transmit, distribute,
            perform and display, modify and create derivative works of the
            Customer Data solely to the extent necessary to provide TestTheTest
            Services.
          </TermsParagraph>
          <TermsParagraph>
            Customer is solely responsible for the accuracy, content and
            legality of all their data. Customer shall ensure that their use of
            TestTheTest Services is at all times compliant with Customer&apos;s
            privacy policies and all applicable local, state, federal and
            international law, regulations and conventions, including without
            limitation those related to data privacy, international
            communications and the exportation of technical or personal data.
            Customer represents and warrants to TestTheTest that Customer has
            sufficient rights in the Customer Data to authorize TestTheTest to
            process, distribute and display the Customer Data as contemplated by
            this Agreement, and that the Customer Data and its use hereunder
            will not violate or infringe the rights of any third party.
          </TermsParagraph>
          <TermsParagraph>
            Provider will take reasonable technical and organizational measures
            designed to keep the Customer Data secure and to protect it against
            accidental loss or unlawful destruction, alteration, disclosure or
            access.
          </TermsParagraph>

          <TermsHead2>2. Subscription Term, Fees &amp; Payment</TermsHead2>

          <TermsParagraph>
            The Subscription Term shall begin on the Effective Date and expire
            after the twelve months, or after the Minimum Term expired if one is
            set in the Order Form. After this time expires, the Subscription
            Term will automatically be renewed for successive one-month renewal
            terms, unless either Customer or Provider sends written notice of
            its intent not to renew, no later than 30 days prior to the end of
            the then-current term.
          </TermsParagraph>
          <TermsParagraph>
            Invoices are due on the first date of each calendar month. In case
            the Effective Date falls on any other date than the 1st day of the
            month, the first invoice will be prorated from the Effective Date
            through the end of that calendar month. The invoice will be issued
            in electronic format. Customer shall provide at least the following
            information in writing: (1) full registered company name, (2)
            registered office address, (3) tax code or any additional code and
            relevant information required under applicable law.
          </TermsParagraph>
          <TermsParagraph>
            Customer agrees to pay the agreed Subscription Fee and all
            applicable taxes for the TestTheTest Services monthly. The first and
            last payment amounts will be prorated based on the Subscription Term
            start date.
          </TermsParagraph>
          <TermsParagraph>
            All fees shall be paid by Customer within seven (7) days of the
            effective date of the applicable invoice. All fees due under the
            Agreement are non-cancelable and the sums paid non-refundable.
            Provider will invoice Customer for all applicable taxes including,
            but not limited to, VAT, GST, sales tax, consumption tax and service
            tax. Customer will make all payments free and clear of any
            withholding or other taxes; any such taxes imposed on payments by
            Customer hereunder will be Customer&apos;s sole responsibility.
          </TermsParagraph>
          <TermsParagraph>
            Disputed Fees. If Customer believes that TestTheTest has billed
            Customer incorrectly, Customer must contact TestTheTest no later
            than fifteen (15) days after the closing date on the first billing
            statement in which the alleged error or problem appeared, in order
            to receive an adjustment or credit.
          </TermsParagraph>
          <TermsParagraph>
            Suspension of Services. If Customer&apos;s account is fifteen (15)
            days or more overdue, in addition to any of its other rights or
            remedies, TestTheTest reserves the right to suspend Customer&apos;s
            access to Services without liability to Customer until such amounts
            are paid in full.
          </TermsParagraph>
          <TermsParagraph>
            Termination of this Agreement or the Order Form shall not entitle
            the Customer to any refund, credit, or other compensation from
            Provider under the Agreemtn or any other agreement or from any third
            party.
          </TermsParagraph>

          <TermsHead2>3. License</TermsHead2>

          <TermsParagraph>
            Subject to Terms, Customer is hereby granted a non-exclusive,
            non-transferable license to use the Service and other proprietary
            information.
          </TermsParagraph>
          <TermsParagraph>
            Provider, its partners and licensors shall retain all right, title,
            know-how, and other proprietary and intellectual property rights in
            the Service. Customer does not acquire any rights in the Service,
            except as specified and granted in this Agreement, and shall not
            remove any proprietary, copyright, patent, trademark, design right,
            trade secret or any other proprietary rights from the software.
            Customer shall not (1) modify, resell, distribute, or create
            derivative works based on the software or any part thereof (2) make
            the Service available to anyone other than the Customer itself (3)
            sell, resell, license, sublicense, distribute, rent or lease the
            Service or include it in a service bureau or outsourcing offering,
            or (4) reverse engineer, decompile, disassemble, or otherwise seek
            to obtain the source code to TestTheTest Services, except to the
            extent permitted by applicable law.
          </TermsParagraph>
          <TermsParagraph>
            Customer agrees that Provider and its partners retain all right,
            title and interest (including all patent, copyright, trade secret
            and other intellectual property rights) in and to TestTheTest
            Services and any other TestTheTest deliverables and any and all
            related and underlying software, databases, any non-Customer
            specific data and statistical data, technology, reports and
            documentation and all copies, modifications and derivative works
            thereof.
          </TermsParagraph>

          <TermsHead2>4. Confidentiality</TermsHead2>

          <TermsParagraph>
            “Confidential Information” means all information disclosed by a
            party (“Disclosing Party”) to the other party (“Receiving Party”),
            whether orally or in writing, that is designated as confidential or
            that reasonably should be understood to be confidential given the
            nature of the information and the circumstances of disclosure.
            Confidential Information of Customer includes Customer Data;
            Confidential Information of Provider includes the Services and
            Content, and the terms and conditions of this Agreement.
            Confidential Information of each party includes business and
            marketing plans, technology and technical information, product plans
            and designs, and business processes disclosed by such party.
          </TermsParagraph>
          <TermsParagraph>
            Confidential Information does not include any information that (i)
            is or becomes generally known to the public without breach of any
            obligation (ii) was known to the Receiving Party prior to its
            disclosure (iii) is received from a third party without knowledge of
            any breach of any obligation (iv) was independently developed by the
            Receiving Party.
          </TermsParagraph>
          <TermsParagraph>
            The Receiving Party will use the same degree of care that it uses to
            protect the confidentiality of its own confidential information of
            like kind to (i) not use any Confidential Information of the
            Disclosing Party for any purpose outside the scope of this Agreement
            and (ii) except as otherwise authorized by the Disclosing Party in
            writing, limit access to Confidential Information of the Disclosing
            Party to those of its and its Affiliates&apos; employees and
            contractors who need that access for purposes consistent with this
            Agreement; (iii) Receiving Party may disclose Confidential
            Information of the Disclosing Party to the extent compelled by law
            to do so.
          </TermsParagraph>

          <TermsHead2>5. Liability</TermsHead2>

          <TermsParagraph>
            NEITHER PARTY WILL HAVE ANY LIABILITY ARISING OUT OF OR RELATING TO
            THIS AGREEMENT FOR: (1) THE OTHER PARTY&apos;S LOST REVENUES; (2)
            FOR ERROR OR INTERRUPTION OF USE OR FOR LOSS OR INACCURACY OR
            CORRUPTION OF DATA OR COST OF PROCUREMENT OF SUBSTITUTE GOODS,
            SERVICES OR TECHNOLOGY; (3) INDIRECT, SPECIAL, INCIDENTAL OR
            CONSEQUENTIAL LOSSES (WHETHER OR NOT FORESEEABLE OR CONTEMPLATED BY
            THE PARTIES AT THE EFFECTIVE DATE); OR (4) EXEMPLARY OR PUNITIVE
            DAMAGES; EVEN IF THE PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF
            ANY OF THE FOREGOING TYPES OF LOSSES OR DAMAGES.
          </TermsParagraph>
          <TermsParagraph>
            EACH PARTY&apos;S AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO
            THIS AGREEMENT WILL NOT EXCEED THE AMOUNT CUSTOMER PAID TO PROVIDER
            UNDER THIS AGREEMENT FOR THE 6 MONTHS PRECEDING THE SUBJECT CLAIM.
          </TermsParagraph>
          <TermsParagraph>
            TESTTHETEST SERVICES, INCLUDING WITHOUT LIMITATION THE THIRD-PARTY
            CODE ARE PROVIDED “AS IS”. PROVIDER DOES NOT GUARANTEE ANY RESULTS
            FROM USING THE SERVICES. PROVIDER DOES NOT WARRANT THAT THE SERVICES
            WILL BE UNINTERRUPTED OR ERROR FREE OR MEET CUSTOMER&apos;S
            REQUIREMENTS; NOR DOES IT MAKE ANY WARRANTY AS TO THE RESULTS THAT
            MAY BE OBTAINED FROM USE OF THE SERVICES. NEITHER PROVIDER NOR ANY
            OF ITS SUPPLIERS MAKES ANY OTHER WARRANTIES, EXPRESS OR IMPLIED,
            STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE OR
            NON-INFRINGEMENT. CUSTOMER MAY HAVE OTHER STATUTORY RIGHTS. HOWEVER,
            TO THE FULL EXTENT PERMITTED BY LAW, ANY STATUTORILY REQUIRED
            WARRANTIES SHALL BE LIMITED AS PROVIDED HEREIN. PROVIDER SHALL NOT
            BE LIABLE FOR DELAYS, INTERRUPTIONS, SERVICE FAILURES AND OTHER
            PROBLEMS INHERENT IN USE OF THE INTERNET AND ELECTRONIC
            COMMUNICATIONS OR OTHER SYSTEMS OUTSIDE THE REASONABLE CONTROL OF
            PROVIDER.
          </TermsParagraph>

          <TermsHead2>6. Mutual Indemnification</TermsHead2>

          <TermsParagraph>
            Provider will defend Customer against any claim, demand, suit or
            proceeding made or brought against Customer by a third party
            alleging that Service infringes or misappropriates such third
            party&apos;s intellectual property rights, provided Customer (a)
            promptly gives Provider written notice of the claim (b) gives
            Provider sole control of the defense and settlement, (c) gives
            Provider all reasonable assistance, and (d) the claim against
            Customer didn&apos;t arise from Customer&apos;s breach of this
            Agreement. If Provider receives information about an infringement or
            misappropriation claim related to a Service, Provider may in its
            discretion and at no cost to Customer (i) modify the Services so
            that they are no longer claimed to infringe or misappropriate, (ii)
            obtain a license for Customer&apos;s continued use of that Service
            in accordance with this Agreement, or (iii) terminate
            Customer&apos;s subscriptions for that Service upon 30 days&apos;
            written notice and refund Customer any prepaid fees covering the
            remainder of the term of the terminated subscriptions.
          </TermsParagraph>
          <TermsParagraph>
            Customer will defend Provider against any claim, demand, suit or
            proceeding made or brought against Provider by a third party (a)
            alleging that Customer&apos;s activity in connection with Services
            infringes or misappropriates such third party&apos;s intellectual
            property rights, or (b) arising from (i) Customer&apos;s use of the
            Services or Content in an unlawful manner or in violation of the
            Agreement (ii) any Customer Data or Customer&apos;s use of Customer
            Data with the Services, and will indemnify Provider from any
            damages, attorney fees and costs finally awarded against Provider.
          </TermsParagraph>

          <TermsHead2>7. General Terms</TermsHead2>

          <TermsParagraph>
            Severability. If any provision of this Agreement shall be adjudged
            by any court of competent jurisdiction to be unenforceable or
            invalid, that provision shall be limited to the minimum extent
            necessary so that this Agreement shall otherwise remain in effect.
          </TermsParagraph>
          <TermsParagraph>
            Entire Agreement. This Agreement together with its applicable
            amendments, addenda, Order Forms and Statement of Work documents is
            the complete and exclusive statement of the mutual understanding of
            the parties and supersedes and cancels all previous written and oral
            agreements and communications relating to the subject matter of this
            Agreement. Customer acknowledges that TestTheTest Services is an
            online, subscription based product, and that in order to provide
            improved customer experience TestTheTest may make changes to
            TestTheTest Services.
          </TermsParagraph>
          <TermsParagraph>
            The parties to this Agreement are independent contractors. There is
            no relationship of partnership, joint venture, employment, franchise
            or agency created hereby between the parties. Neither party will
            have the power to bind the other or incur obligations on the other
            party&apos;s behalf without the other party&apos;s prior written
            consent.
          </TermsParagraph>
          <TermsParagraph>
            Export Control. In its use of TestTheTest Services, Customer agrees
            to comply with all export and import laws and regulations of the
            United States and other applicable jurisdictions. Without limiting
            the foregoing, (i) Customer represents and warrants that it is not
            listed on any U.S. government list of prohibited or restricted
            parties or located in (or a national of) a country that is subject
            to a U.S. government embargo or that has been designated by the U.S.
            government as a “terrorist supporting” country and (ii) Customer
            shall not (and shall not permit any of its Permitted Users or others
            to) access or use the TestTheTest Services in violation of any U.S.
            export embargo, prohibition or restriction.
          </TermsParagraph>
          <TermsParagraph>
            Jurisdiction. This agreement shall be governed by United States law
            and the Parties shall hereby submit to the exclusive jurisdiction of
            the Courts of New York State.
          </TermsParagraph>
        </Box>
      </PaddingContainer>
    </PageContainer>
  );
};

export default Screen;
